import React, { useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import { Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { Toasts } from "view/components/Toasts";
import PersonPlaceholder from "assets/images/Placeholders/OrganizationPlaceholder.png";
import { closeNotificationsCenterModalAction } from "store/modals/reducer.actions";
import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "redux";
import XCloseIcon from "assets/icons/HeroIcons/XCloseIcon";
import { useNavigate } from "react-router-dom";
import { SideNav } from "store/sideBar/initialState";
import ArrowUpRightExpandIcon from "assets/icons/HeroIcons/ArrowUpRightExpandIcon";
import { CheveronRightIcon } from "assets/icons/HeroIcons";
import usePermissions from "hooks/usePermissions";
import { TailSpin } from "react-loader-spinner";
import useCustomBranding from "hooks/useCustomBranding";
import { fetchLimitedNotificationsAction } from "store/limitedNotifications";
import useDateFormatter from "hooks/formatDateWithPattern";
import apiLibrary from "services/api";
import { fetchUnreadNotificationsStatusAction } from "store/unreadNotifications";

export const NotificationsCenterModal = () => {
  const { isCollapsed } = useSelector<RootState, SideNav>(
    (state) => state?.sideBar,
  );
  const { primaryColor } = useCustomBranding();
  const { notificationsCenterModal } = useSelector(
    (state: RootState) => state.modals,
  );
  const { notifications } = usePermissions();
  const [loading, setIsLoading] = useState(false);

  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  const navigate = useNavigate();
  const { timeSince } = useDateFormatter();

  const limitedNotifications = useSelector(
    (state: RootState) => state.limitedNotifications.data,
  );
  const isLoading = useSelector(
    (state: RootState) => state.limitedNotifications.stateIs === "Pending",
  );

  const handleClose = () => {
    dispatch(closeNotificationsCenterModalAction());
  };

  const handleNotificationClick = async (notificationId: number) => {
    setIsLoading(true);
    try {
      await apiLibrary.NotificationCenter.readNotification(notificationId);
      dispatch(fetchLimitedNotificationsAction());
      Toasts.success("Notification marked as read");
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message ?? error.message;
      Toasts.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    if (limitedNotifications.unreadNotificationCount === 0) return;
    setIsLoading(true);

    try {
      await apiLibrary.NotificationCenter.readAllNotifications();
      dispatch(fetchLimitedNotificationsAction());
      dispatch(fetchUnreadNotificationsStatusAction());
      Toasts.success("All notifications marked as read");
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message ?? error.message;
      Toasts.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeRoute = () => {
    navigate("/notification-center/list");
    handleClose();
  };

  useEffect(() => {
    dispatch(fetchLimitedNotificationsAction());
  }, [dispatch]);

  return (
    <Modal
      open={notificationsCenterModal.isOpen}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      disableAutoFocus={true}
      aria-describedby="modal-modal-description"
      className="border-none"
      BackdropProps={{
        style: {
          backgroundColor: "transparent",
          boxShadow: "none",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "2%",
          left: `${isCollapsed ? "250px" : "408px"}`,
          transform: "translate(-50%, 0%)",
          backgroundColor: "#ffff",
          borderRadius: "10px",
          zIndex: "50px",
          boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        }}
        className={`dark:bg-secondaryLight w-[360px] ${
          limitedNotifications.unreadNotificationCount > 8
            ? "h-screen"
            : "h-screen"
        } relative`}
      >
        <div className="flex-col h-full flex-">
          <div className="flex py-2 justify-between items-center h-[6vh]">
            <div className="w-2/5 px-2 mt-[2px] dark:text-textMain">
              Unread: {limitedNotifications.unreadNotificationCount ?? 0}
            </div>
            <div className="flex items-center justify-end w-3/5 px-3">
              <div className="mr-4">
                {notifications.canCreateNotifications && (
                  <button
                    type="button"
                    className={`mt-[2px] dark:text-textMain text-primary ${
                      !notifications.canCreateNotifications ||
                      isLoading ||
                      loading ||
                      limitedNotifications.unreadNotificationCount === 0
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    onClick={handleMarkAllRead}
                    disabled={
                      !notifications.canCreateNotifications ||
                      isLoading ||
                      loading ||
                      limitedNotifications.unreadNotificationCount === 0
                    }
                  >
                    Mark all as read
                  </button>
                )}
              </div>

              <div className="flex gap-2">
                {notifications.canViewNotifications && (
                  <button onClick={handleChangeRoute}>
                    <ArrowUpRightExpandIcon className={"w-5 h-5 mt-1"} />
                  </button>
                )}
                <button onClick={handleClose}>
                  <XCloseIcon className="" fill="#6F747B" />
                </button>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="loader max-h-[88vh] min-h-[88vh] justify-center items-center flex w-full">
              <TailSpin
                height="50"
                width="50"
                color={primaryColor}
                ariaLabel="tail-spin-loading"
                radius="2"
                wrapperStyle={{}}
                wrapperClass="tailspin-loader"
                visible={true}
              />
            </div>
          ) : (
            <div className="flex flex-col max-h-[88vh] min-h-[88vh] overflow-y-auto">
              {limitedNotifications.notifications?.map(
                (notification: any, index: number) => {
                  return (
                    <div
                      className={`mb-1 ${
                        !notification.isRead &&
                        "bg-bgHighMidlight cursor-pointer dark:bg-secondaryLight"
                      }`}
                      key={notification.id ?? index}
                      onClick={
                        !notification.isRead &&
                        notifications.canReadNotifications
                          ? () => handleNotificationClick(notification.id)
                          : undefined
                      }
                    >
                      <div
                        className={`flex px-4 py-3 ${
                          !notifications.canReadNotifications &&
                          !notification.isRead &&
                          "opacity-50 cursor-not-allowed"
                        }`}
                      >
                        <div className="flex items-center">
                          {!notification.isRead && (
                            <div className="w-2 h-2 mr-2 rounded-full bg-primaryMidLight"></div>
                          )}

                          <div className="w-[35px] h-[35px] rounded-full">
                            <img
                              src={
                                notification?.profileImage
                                  ? notification?.profileImage
                                  : PersonPlaceholder
                              }
                              alt={notification?.title}
                              className="object-cover w-full h-full rounded-full"
                            />
                          </div>
                        </div>
                        <div className="w-full ml-3">
                          <p className="text-xs font-normal text-textLight">
                            {timeSince(notification?.createdAt)}
                          </p>

                          <p
                            className="w-full mr-1 overflow-hidden text-sm font-normal leading-5 custom-html-content text-textNeutralGray dark:text-caption font-Overpass overflow-ellipsis line-clamp-2"
                            dangerouslySetInnerHTML={{
                              __html: notification?.description,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                },
              )}
            </div>
          )}

          <div className="px-2 flex items-center justify-end gap-1 cursor-pointer h-[4vh]">
            <button className="flex items-center" onClick={handleChangeRoute}>
              <p className="dark:text-textMain">View more</p>
              <CheveronRightIcon />
            </button>
          </div>
        </div>
      </Box>
    </Modal>
  );
};
